import classNames from "classnames";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Table from "components/DesignSystem/Table/V2/Table";
import { CaretDown } from "components/icons/CaretDown";
import { TaskSlider } from "components/TaskSlider/TaskSlider";
import { useMemo, useState } from "react";
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { getEntityTaskTableColumns } from "./EntityTaskTableColumns";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useQuery } from "hooks/useQuery";
import { openActionCentreSlider } from "store/slices/channelInfoSlider";
import { useDispatch } from "react-redux";
import { TaskSkeletonComponent } from "./EntityTaskTable";
import { TaskTableRow } from "./TaskTableRow";
import { useWindowSize } from "hooks/useWindowSize";
import { FILTER_HIGHLIGHT_TASK_ALARM_UUID } from "constants/task";

export const TaskTableTaskSkeletonComponent = ({
  noBorder,
}: {
  noBorder?: boolean;
}) => (
  <div
    className={classNames("t-p-4 t-pl-0", {
      "t-border t-border-solid t-border-t-0 t-border-l-0 t-border-r-0 t-border-neutral-0":
        !noBorder,
    })}
  >
    <TaskSkeletonComponent />
  </div>
);

export const TaskTableBodyPerSeason = ({
  tasks,
  season,
  isFetching,
}: {
  tasks: TaskDeadlineOverviewItem[];
  season: string;
  isFetching: boolean;
}) => {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const { isCpa } = useRoleBasedView();
  const { isMobile } = useWindowSize();
  const query = useQuery();
  const isLaunchingTask = Boolean(query.get("check_for_tasks"));
  const highlightUuid = query.get(FILTER_HIGHLIGHT_TASK_ALARM_UUID);
  const dispatch = useDispatch();
  const [showDefaultChat, setShowDefaultChat] = useState(false);

  const [showActionDetailsOnly, setShowActionDetails] =
    useState<boolean>(false);

  const processedData = useMemo(() => {
    const taskList = tasks || [];
    const templateMap = new Map<string, Array<any>>();

    taskList.forEach((task: any) => {
      if (!templateMap.get(task?.template?.uuid)) {
        templateMap.set(task?.template?.uuid, []);
      }

      templateMap.get(task?.template?.uuid)?.push(task);
    });

    const subTaskTemplateMap = taskList.reduce((acc, task) => {
      if (
        task.parent_task_template_id &&
        templateMap.get(task.parent_task_template_id)?.length
      ) {
        return {
          ...acc,
          [task.parent_task_template_id]: [
            ...(acc[task.parent_task_template_id] || []),
            task,
          ],
        };
      }

      return acc;
    }, {} as Record<string, any[]>);

    const nestedSubtasks = new Set<TaskDeadlineOverviewItem>();

    const tasksAndTheirSubtasks = taskList.map((parent) => {
      const rawSubTasks = subTaskTemplateMap[parent?.template?.uuid]?.filter(
        (subTask) => subTask.entity?.uuid === parent.entity?.uuid
      );

      rawSubTasks?.forEach((subTask) => nestedSubtasks.add(subTask));

      const subTasks = rawSubTasks?.map((task, i, arr) => {
        const isLast = i === arr.length - 1;

        return {
          ...task,
          isSubTask: true,
          isLast,
        };
      });

      return {
        ...parent,
        subRows: subTasks || [],
        toggleAllRowsExpanded: true,
      };
    });

    return tasksAndTheirSubtasks.filter(
      (_, index) => !nestedSubtasks.has(taskList[index])
    );
  }, [tasks]);

  const handleOpenActions = (taskId: string) => {
    setSelectedRowId(taskId);
    setShowActionDetails(true);
    dispatch(openActionCentreSlider());
  };

  const handleOpenChat = (taskId: string) => {
    setSelectedRowId(taskId);
    setShowDefaultChat(true);
  };

  const table = useReactTable({
    data: processedData,
    columns: getEntityTaskTableColumns(isCpa),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.subRows,
    state: {
      expanded: true,
    },
    enableExpanding: true,
    autoResetExpanded: false,
    defaultColumn: {
      minSize: 5,
    },
    enableRowSelection: true,
    meta: {
      openActions: (taskId: string) => handleOpenActions(taskId),
      openSliderChat: (taskId: string) => handleOpenChat(taskId),
    },
  });

  const handleTaskActionDetailsClose = () => {
    setSelectedRowId(null);
    setShowActionDetails(false);
    setShowDefaultChat(false);
  };

  const handleRowClick = (taskId: string | null) => {
    setSelectedRowId(taskId || null);
    setShowActionDetails(false);
  };

  return (
    <>
      <Table.Row
        className="t-bg-neutral-0 t-cursor-pointer hover:!t-bg-neutral-0"
        onRowClick={() => setIsExpanded((prev) => !prev)}
      >
        <Table.Cell
          colSpan={getEntityTaskTableColumns(isCpa).length}
          className="!t-p-1.5"
        >
          <div className="t-text-subtext-sm t-text-text-60">
            Tax year {season}
            <CaretDown
              className={classNames(
                "t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out t-ml-2",
                {
                  "t-rotate-180": isExpanded,
                  "t-rotate-0": !isExpanded,
                }
              )}
            />
          </div>
        </Table.Cell>
      </Table.Row>
      {isExpanded && (
        <>
          {isFetching && !isLaunchingTask ? (
            <Table.Row>
              <Table.Cell colSpan={getEntityTaskTableColumns(isCpa).length}>
                <div className="t-h-full t-w-full">
                  <TaskTableTaskSkeletonComponent />
                  <TaskTableTaskSkeletonComponent />
                  <TaskTableTaskSkeletonComponent noBorder />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            table
              .getRowModel()
              .rows.map((row) => (
                <TaskTableRow
                  key={row.id}
                  row={row}
                  highlightUuid={highlightUuid}
                  onRowClick={handleRowClick}
                  isMobile={isMobile}
                />
              ))
          )}
        </>
      )}
      {selectedRowId && (
        <TaskSlider
          taskId={selectedRowId}
          isOpen
          onClose={handleTaskActionDetailsClose}
          showActionDetailsOnly={showActionDetailsOnly}
          defaultTab={showDefaultChat ? "CHAT" : "DETAILS"}
        />
      )}
    </>
  );
};
